/**
 * LegacyMigrationNotice
 *
 * The member's current subscription lives in an OLD Stripe account, so this
 * purchase is charged at FULL price and the unused time comes back as a cash
 * refund once the replacement subscription exists (Stripe balances cannot move
 * between accounts). Nothing reduces the total, which is exactly why the promise
 * has to be stated wherever the amount is shown — the cart AND the payment step,
 * where the member actually commits the money.
 *
 * Rendered from order properties (not FE state), so it survives a hard refresh.
 */

import type { MembershipPlanChange } from "@/types/cart";
import { useLocalizedTexts, t } from "@/utils/translations";
import { formatMinorUnitsAsCurrency } from "@/utils/priceFormat";
import styles from "./LegacyMigrationNotice.module.css";

interface LegacyMigrationNoticeProps {
  /** The cart's membership change; the notice renders only for a legacy migration. */
  membershipChange: MembershipPlanChange | null | undefined;
  className?: string;
}

export function LegacyMigrationNotice({
  membershipChange,
  className,
}: LegacyMigrationNoticeProps) {
  const localizedTexts = useLocalizedTexts();

  if (!membershipChange?.isLegacyMigration) {
    return null;
  }

  const { legacyRefundEstimateMinor, legacyRefundCurrency } = membershipChange;

  // A refund we cannot quote is not a refund we promise an amount for: the
  // estimate is missing whenever the legacy subscription had no invoice to
  // refund against, and "approximately null" would be worse than saying only
  // that the old subscription ends.
  const body =
    legacyRefundEstimateMinor !== null &&
    legacyRefundEstimateMinor > 0 &&
    legacyRefundCurrency
      ? t(
          localizedTexts,
          "Tracker.Cart.PlanChange.LegacyBannerBody",
          "When you complete this purchase, your current subscription will be cancelled and approximately {amount} will be refunded for the unused time."
        ).replace(
          "{amount}",
          formatMinorUnitsAsCurrency(
            legacyRefundEstimateMinor,
            legacyRefundCurrency
          )
        )
      : t(
          localizedTexts,
          "Tracker.Cart.PlanChange.LegacyBannerBodyNoRefund",
          "When you complete this purchase, your current subscription will be cancelled."
        );

  return (
    <div
      className={className ? `${styles.notice} ${className}` : styles.notice}
      role="note"
    >
      <p className={styles.heading}>
        {t(
          localizedTexts,
          "Tracker.Cart.PlanChange.LegacyBannerHeading",
          "Subscription migration"
        )}
      </p>
      <p className={styles.body}>{body}</p>
    </div>
  );
}
